/**
 * When periods open and close.
 *
 * The client's requirement, in their words: everything opens on the 3rd, except one project
 * that opens on the 4th and another that opens on the 28th of the *previous* month because
 * it reports ahead of the cycle. So: one default, and a short list of exceptions.
 *
 * The design problem is that "the 3rd" is ambiguous — the 3rd of which month? The period's
 * own, or the one after it? Most of the time it is the month after, which is why it goes
 * unsaid, and the project that opens on the 28th of the month *before* is exactly the case
 * the unsaid convention cannot express. So each edge is a day and a month, and the screen
 * shows the resolved dates for a real period underneath, because nobody should have to do
 * calendar arithmetic in their head to check they have configured what they meant.
 *
 * Administrators only for changes. Readable by everyone, because "why can I not type in this
 * cell?" is asked by the people entering the data, and an answer only an administrator can
 * see is not an answer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import { useSession } from '@/app/session'
import { Icon } from '@/components/Icon'
import { Empty, Failed, Skeleton } from '@/components/states'
import { ScheduleEditor } from '@/features/periods/ScheduleEditor'

export type WindowEdge = {
  day: number
  month_offset: number
  describes: string
}

export type ScheduleRow = {
  project_id: string | null
  project_title: string | null
  project_number: string | null
  opens: WindowEdge
  closes: WindowEdge
  note: string | null
  inherited: boolean
  example: { period: string; opens_on: string; closes_after: string }
}

export type ScheduleView = {
  default: ScheduleRow
  overrides: ScheduleRow[]
  example_period: string
}

export function PeriodSchedule() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isAdmin = useSession((state) => state.hasRole('pmo_admin', 'platform_admin'))
  const [editing, setEditing] = useState<ScheduleRow | 'new' | null>(null)

  const schedule = useQuery({
    queryKey: ['period-schedule'],
    queryFn: ({ signal }) => api.get<ScheduleView>('/period-schedule', signal),
  })

  const clear = useMutation({
    mutationFn: (projectId: string) => api.delete(`/period-schedule/${projectId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['period-schedule'] })
      void queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const day = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' })
  const when = (iso: string) => day.format(new Date(`${iso}T00:00:00`))

  if (schedule.isPending) {
    return (
      <section className="card card--pad" data-testid="period-schedule">
        <Skeleton rows={3} />
      </section>
    )
  }

  if (schedule.isError) {
    return (
      <section className="card card--pad" data-testid="period-schedule">
        <Failed
          message={
            schedule.error instanceof ApiError
              ? schedule.error.friendly
              : String(schedule.error)
          }
          onRetry={() => void schedule.refetch()}
        />
      </section>
    )
  }

  const view = schedule.data
  const example = view.example_period

  return (
    <section className="card card--pad" data-testid="period-schedule">
      <div className="builder__head">
        <div>
          <h2 className="card__title">{t('schedule.title')}</h2>
          <p className="card__caption card__caption--note">{t('schedule.intro')}</p>
        </div>
        {isAdmin ? (
          <div className="builder__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => setEditing('new')}
              data-testid="schedule-add"
            >
              {t('schedule.addException')}
            </button>
          </div>
        ) : null}
      </div>

      {/* The default, given its own block rather than a row in the table. It governs every
          project that is not listed underneath, which is almost all of them, and a table
          would make it look like one case among several. */}
      <div className="schedule__default" data-testid="schedule-default">
        <div className="schedule__defaultText">
          <p className="schedule__label">{t('schedule.everyProject')}</p>
          <p className="schedule__rule">
            {t('schedule.opensOn')} <strong>{view.default.opens.describes}</strong>
            {' · '}
            {t('schedule.closesAfter')} <strong>{view.default.closes.describes}</strong>
          </p>
          <p className="schedule__example">
            {t('schedule.forExample', {
              period: example,
              opens: when(view.default.example.opens_on),
              closes: when(view.default.example.closes_after),
            })}
          </p>
        </div>
        {isAdmin ? (
          <button
            type="button"
            className="button button--quiet button--small"
            onClick={() => setEditing(view.default)}
            data-testid="schedule-edit-default"
          >
            {t('builder.edit')}
          </button>
        ) : null}
      </div>

      <h3 className="schedule__heading">{t('schedule.exceptions')}</h3>

      {view.overrides.length === 0 ? (
        <Empty title={t('schedule.noExceptions')} hint={t('schedule.noExceptionsHint')} />
      ) : (
        <div className="grid__scroll" data-scroll="1">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('nav.projects')}</th>
                <th scope="col">{t('schedule.opens')}</th>
                <th scope="col">{t('schedule.closes')}</th>
                <th scope="col">{t('schedule.forPeriod', { period: example })}</th>
                <th scope="col">{t('schedule.why')}</th>
                <th scope="col">
                  <span className="visually-hidden">{t('grid.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {view.overrides.map((row) => (
                <tr key={row.project_id} data-testid="schedule-row">
                  <td>
                    <span className="schedule__project">{row.project_title ?? '—'}</span>
                    <span className="schedule__number numeric">{row.project_number ?? ''}</span>
                  </td>
                  <td>{row.opens.describes}</td>
                  <td>{row.closes.describes}</td>
                  {/* The resolved dates, so a rule that reads plausibly and resolves to
                      something surprising is visible without anyone counting on fingers. */}
                  <td className="table__muted numeric">
                    {when(row.example.opens_on)} – {when(row.example.closes_after)}
                  </td>
                  <td className="table__muted">{row.note ?? '—'}</td>
                  <td className="table__numeric">
                    {isAdmin ? (
                      <span className="builder__actions">
                        <button
                          type="button"
                          className="button button--quiet button--small"
                          onClick={() => setEditing(row)}
                          data-testid="schedule-edit"
                        >
                          {t('builder.edit')}
                        </button>
                        <button
                          type="button"
                          className="button button--quiet button--small"
                          onClick={() => row.project_id && clear.mutate(row.project_id)}
                          data-testid="schedule-clear"
                        >
                          {t('schedule.useDefault')}
                        </button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {clear.isError ? (
        <p className="modal__error" role="alert" data-testid="schedule-error">
          {clear.error instanceof ApiError ? clear.error.friendly : String(clear.error)}
        </p>
      ) : null}

      <p className="card__note">
        <Icon name="lock" size={13} />
        <span>{t('schedule.note')}</span>
      </p>

      {editing ? (
        <ScheduleEditor
          row={editing === 'new' ? null : editing}
          taken={view.overrides.map((row) => row.project_id ?? '')}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void queryClient.invalidateQueries({ queryKey: ['period-schedule'] })
            void queryClient.invalidateQueries({ queryKey: ['audit'] })
          }}
        />
      ) : null}
    </section>
  )
}
